import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as MessageService from 'services/agents/message';

export const useMessageService = () => {
  const { token } = useSelector((store) => store.user_data);

  const createMessage = async (data, options = {}) => {
    const { error, payload: message } = await MessageService.create({
      ...options,
      data,
      token
    });

    if (error) {
      toast.error('Cannot create message at this time');
      return { message: {} };
    }

    toast.success('Message created successfully.');
    return { message };
  };

  const deleteMessage = async (id, options = {}) => {
    const { error } = await MessageService.deleteOne(id, { ...options, token });

    if (error) {
      toast.error('Unable to delete message at this time.');
      return false;
    }

    toast.success('Message deleted successfully');
    return true;
  };

  const fetchMessage = async (id, options = {}) => {
    const { error, payload: message } = await MessageService.readById(id, { ...options, token });

    if (error) {
      toast.error(error);
      return { message: null };
    }

    return { message };
  };

  const fetchMessages = async (options = {}) => {
    const { error, payload } = await MessageService.read({ ...options, token });

    if (error) {
      toast.error(error);
      return { messages: [], size: 0 };
    }

    const {
      data: messages,
      meta: { size }
    } = payload;
    return { messages, size };
  };

  const searchMessages = async (keys, keyword, options = {}) => {
    const { error, payload } = await MessageService.search(keys, keyword, { ...options, token });

    if (error) {
      toast.error('Unable to fetch messages at this time.');
      return { messages: [], size: 0 };
    }

    const {
      data: messages,
      meta: { size }
    } = payload;
    return { messages, size };
  };

  const updateMessage = async (id, options = {}) => {
    const { error, payload } = await MessageService.updateById(id, { ...options, token });

    if (error) {
      toast.error('Unable to update message at this time.');
      return false;
    }

    toast.success('Message updated successfully');
    return payload || true;
  };

  const updateMessages = async (options = {}) => {
    const { error } = await MessageService.update({ ...options, token });

    if (error) {
      toast.error('Unable to update messages at this time.');
      return false;
    }

    toast.success('Messages updated successfully');
    return true;
  };

  const deleteMessages = async (options = {}) => {
    const { error } = await MessageService.deleteMany({ ...options, token });

    if (error) {
      toast.error('Unable to delete messages at this time.');
      return false;
    }

    toast.success('Messages deleted successfully');
    return true;
  };

  const sendMessage = async (id, options = {}) => {
    const { error } = await MessageService.sendById(id, { ...options, token });

    if (error) {
      toast.error('Unable to send message at this time.');
      return false;
    }

    toast.success('Message sent successfully');
    return true;
  };

  return {
    createMessage,
    deleteMessage,
    fetchMessage,
    fetchMessages,
    searchMessages,
    updateMessage,
    updateMessages,
    deleteMessages,
    sendMessage
  };
};
